<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <component
    :is="inline ? 'div' : PrimeDialog"
    v-bind="
      inline
        ? { class: 'unified-upload-inline' }
        : {
            visible: isVisible,
            'onUpdate:visible': (val) => {
              isVisible = val;
            },
            header: isRequalifyMode
              ? t('classificationPlan.requalify.title')
              : isCreateMode
                ? t('classificationPlan.upload.headerCreate')
                : t('classificationPlan.upload.headerImport'),
            modal: true,
            class: 'unified-upload-dialog',
            style: {
              width:
                (currentStep === configStep || currentStep === natureStep) &&
                isMultiFile
                  ? hasAnyInlineFile
                    ? '85rem'
                    : '70rem'
                  : '50rem',
              maxHeight: '90vh',
            },
            onHide: reset,
          }
    "
  >
    <!-- Stepper -->
    <div v-if="!isLoading" class="stepper-header">
      <PrimeStepper :value="currentStep" linear class="stepper-indicator">
        <PrimeStepList>
          <PrimeStep
            v-for="step in visibleSteps"
            :key="step.value"
            :value="step.value"
          >
            {{ step.label }}
          </PrimeStep>
        </PrimeStepList>
      </PrimeStepper>
      <h4 class="step-title">{{ stepTitle }}</h4>
      <small
        v-if="selectedPlanName && currentStep >= planStep + 1"
        class="step-plan-info"
      >
        {{ t("classificationPlan.summary.generalInfo") }} :
        {{ selectedPlanName }}
      </small>
    </div>

    <div v-if="isLoading" class="loading-container">
      <i class="ri-loader-4-line ri-spin" style="font-size: 2rem"></i>
    </div>

    <!-- ═══════════ STEP: Plan Selection (only when !isForcedPlan) ═══════════ -->
    <div v-if="!isLoading && currentStep === planStep && !isForcedPlan">
      <PrimeSelect
        v-model="selectedPlanName"
        :options="planFiles"
        :placeholder="t('classificationPlan.steps.selectPlan')"
        class="full-width"
        :disabled="planFiles.length === 0"
      />
    </div>

    <!-- ═══════════ STEP: Files (upload mode only) ═══════════ -->
    <div v-if="!isLoading && currentStep === filesStep && !skipFilesStep">
      <PrimeFileUpload
        name="files[]"
        multiple
        :show-upload-button="false"
        :show-cancel-button="false"
        :choose-label="t('classificationPlan.chooseFiles')"
        @select="onFileSelect"
      >
        <template #content>
          <ul v-if="fileConfigs.length" class="upload-file-list">
            <li
              v-for="(fc, index) in fileConfigs"
              :key="index"
              class="upload-file-item"
            >
              <span class="upload-file-name">{{ fc.file.name }}</span>
              <span v-if="fc.file.size" class="upload-file-size">
                {{ formatFileSize(fc.file.size) }}
              </span>
              <PrimeButton
                icon="ri-close-line"
                text
                rounded
                severity="danger"
                size="small"
                :aria-label="t('classificationPlan.multiFile.removeFile')"
                @click="removeFile(index)"
              />
            </li>
          </ul>
          <p v-else class="no-files-msg">
            {{ t("classificationPlan.multiFile.noFiles") }}
          </p>
        </template>
      </PrimeFileUpload>
    </div>

    <!-- ═══════════ STEP: Nature ═══════════ -->
    <div v-if="!isLoading && currentStep === natureStep">
      <!-- ——— Single file mode (or requalify mode) ——— -->
      <div v-if="!isMultiFile" class="single-file-config">
        <template v-if="noNature">
          <div class="no-nature-destination">
            <p class="no-nature-destination-label">
              <i class="ri-file-line"></i>
              {{ t("classificationPlan.destinationLabel") }}
            </p>
            <code class="no-nature-destination-path">{{
              noNatureResolvedPath || "—"
            }}</code>
          </div>
          <PrimeButton
            icon="ri-arrow-left-line"
            text
            size="small"
            :label="t('classificationPlan.multiFile.selectNature')"
            @click="clearNoNature"
          />
        </template>
        <NatureSelector
          v-else
          :natures="natureOptions"
          :modelValue="fileConfigs[0]?.natureCode"
          :allowNoNature="allowNoNature && !isRequalifyMode && !isCreateMode"
          :categories="activeCategories"
          :default-category="defaultCategoryId"
          :disableCategoryFilter="
            forceSingleCategory && !!fileConfigs[0]?.natureCode
          "
          @update:modelValue="onSingleNatureSelect($event)"
          @select-no-nature="onSelectNoNature"
        />
      </div>

      <!-- ——— Multi file mode ——— -->
      <div v-else class="multi-file-config">
        <div class="category-mode-bar">
          <PrimeSelectButton
            v-if="!forceSingleCategory"
            v-model="multiCategoryMode"
            :options="categoryModeOptions"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            size="small"
          />
          <PrimeSelect
            v-if="!multiCategoryMode"
            v-model="globalCategoryId"
            :options="bulkCategoryOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('classificationPlan.multiFile.selectCategory')"
            size="small"
            class="global-category-select"
            :disabled="forceSingleCategory && hasAnyNatureSelected"
            @change="onGlobalCategoryChange"
          />
          <PrimeButton
            v-if="configStore.FILENAME_NATURE_DETECTION"
            icon="ri-sparkling-line"
            text
            rounded
            size="small"
            v-tooltip.top="t('classificationPlan.multiFile.autoDetectNature')"
            @click="retryAutoDetectNature"
          />
        </div>
        <div
          v-if="selectedFiles.length >= 2"
          class="multi-file-toolbar bulk-toolbar"
        >
          <span class="bulk-selection-count">
            {{
              t("classificationPlan.multiFile.selectedCount", {
                count: selectedFiles.length,
              })
            }}
          </span>
          <PrimeSelect
            v-if="multiCategoryMode"
            v-model="bulkCategoryId"
            :options="bulkCategoryOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('classificationPlan.multiFile.selectCategory')"
            size="small"
            @change="applyBulkCategory"
          />
          <PrimeSelect
            v-model="bulkNatureCode"
            :options="bulkNatureOptions"
            optionLabel="name"
            optionValue="code"
            :placeholder="
              !multiCategoryMode && !globalCategoryId
                ? t('classificationPlan.multiFile.selectCategoryFirst')
                : t('classificationPlan.multiFile.bulkSelectNature')
            "
            :disabled="!multiCategoryMode && !globalCategoryId"
            size="small"
            filter
            :filterFields="['name', 'code']"
            :filterPlaceholder="t('classificationPlan.multiFile.searchNature')"
            resetFilterOnHide
            @change="applyBulkNature"
          />
        </div>
        <PrimeDataTable
          :value="fileConfigs"
          v-model:selection="selectedFiles"
          dataKey="file.name"
          size="small"
          rowHover
          :pt="multiFileTablePt"
          :rowGroupMode="multiCategoryMode ? 'subheader' : undefined"
          :groupRowsBy="multiCategoryMode ? getCategoryGroupKey : undefined"
          :expandableRowGroups="multiCategoryMode"
          v-model:expandedRowGroups="expandedGroups"
        >
          <template v-if="multiCategoryMode" #groupheader="{ data }">
            <div class="category-group-header">
              <span>{{ getCategoryGroupLabel(data) }}</span>
              <span class="category-group-count">
                ({{
                  fileConfigs.filter(
                    (fc) =>
                      getCategoryGroupKey(fc) === getCategoryGroupKey(data),
                  ).length
                }})
              </span>
            </div>
          </template>
          <PrimeColumn selectionMode="multiple" headerStyle="width: 3rem" />
          <PrimeColumn
            :header="t('classificationPlan.multiFile.fileName')"
            style="min-width: 12rem"
          >
            <template #body="{ data }">
              <span :title="data.file.name">{{ data.file.name }}</span>
            </template>
          </PrimeColumn>
          <PrimeColumn
            v-if="multiCategoryMode"
            :header="t('classificationPlan.multiFile.category')"
            style="width: 12rem"
          >
            <template #body="{ data }">
              <PrimeSelect
                v-model="data.categoryId"
                :options="categoryOptions"
                optionLabel="label"
                optionValue="value"
                :placeholder="t('classificationPlan.multiFile.selectCategory')"
                class="w-full"
                size="small"
                @change="onCategoryChange(getOriginalIndex(data))"
              />
            </template>
          </PrimeColumn>
          <PrimeColumn
            :header="t('classificationPlan.nature')"
            style="width: 14rem"
          >
            <template #body="{ data }">
              <PrimeSelect
                v-model="data.natureCode"
                :options="
                  getNaturesForCategory(
                    multiCategoryMode ? data.categoryId : globalCategoryId,
                  )
                "
                optionLabel="name"
                optionValue="code"
                :placeholder="
                  !multiCategoryMode && !globalCategoryId
                    ? t('classificationPlan.multiFile.selectCategoryFirst')
                    : t('classificationPlan.multiFile.selectNature')
                "
                :disabled="!multiCategoryMode && !globalCategoryId"
                class="w-full"
                size="small"
                showClear
                filter
                :filterFields="['name', 'code']"
                :filterPlaceholder="
                  t('classificationPlan.multiFile.searchNature')
                "
                resetFilterOnHide
                @change="onNatureChange(getOriginalIndex(data))"
              />
            </template>
          </PrimeColumn>
          <PrimeColumn style="width: 3rem">
            <template #body="{ data }">
              <PrimeButton
                icon="ri-close-line"
                text
                rounded
                severity="danger"
                size="small"
                @click="removeFile(getOriginalIndex(data))"
              />
            </template>
          </PrimeColumn>
        </PrimeDataTable>
      </div>
    </div>

    <!-- ═══════════ STEP: Configuration ═══════════ -->
    <div v-if="!isLoading && currentStep === configStep">
      <!-- ——— Single file mode (or requalify mode) ——— -->
      <div v-if="!isMultiFile" class="single-file-config">
        <div class="selected-nature-info">
          <PrimeTag :value="getActiveNatureName" />
        </div>
        <MetadataFormFields
          :fields="currentFields"
          :modelValue="activeMetadata"
          :requiredFields="requiredFields"
          :disabledFields="preFilledFields"
          :autoExtractedFields="activeAutoExtractedFields"
          :validationErrors="validationErrors"
          :getSearchOptions="getSearchOptions"
          :isSearchLoading="isSearchLoading"
          :dictionaries="dictionaries"
          :lexicons="lexicons"
          :parentFieldOptions="parentFieldOptions"
          @update:modelValue="activeMetadata = $event"
          @load-search="loadSearchOptions($event)"
          @validate="debounceValidate($event, activeMetadata[$event.name])"
        />
        <!-- In create mode, show the file to be created -->
        <div v-if="isCreateMode" class="create-file-info">
          <p>
            <strong>{{ t("classificationPlan.fileToCreate") }} :</strong>
          </p>
          <ul>
            <li v-for="(fc, index) in fileConfigs" :key="index">
              {{ fc.file.name }}
            </li>
          </ul>
        </div>
      </div>

      <!-- ——— Multi file mode ——— -->
      <div v-else class="multi-file-config">
        <div class="category-mode-bar">
          <PrimeSelectButton
            v-if="hasAnyInlineFile"
            v-model="inlineMetadataLayout"
            :options="[
              {
                label: t('classificationPlan.multiFile.layoutRow'),
                value: 'row',
              },
              {
                label: t('classificationPlan.multiFile.layoutCol'),
                value: 'col',
              },
            ]"
            optionLabel="label"
            optionValue="value"
            :allowEmpty="false"
            size="small"
          />
        </div>
        <div v-if="isFullView" class="progress-bar-container">
          <span class="progress-bar-label">
            {{
              t("classificationPlan.multiFile.progressLabel", {
                done: completeCount,
                total: fileConfigs.length,
              })
            }}
          </span>
          <PrimeProgressBar
            :value="
              fileConfigs.length
                ? (completeCount / fileConfigs.length) * 100
                : 0
            "
            :showValue="false"
            :dt="{
              value: { background: 'var(--p-green-500)' },
              root: { height: '6px' },
            }"
            class="progress-bar"
          />
        </div>
        <div v-if="isFullView" class="multi-file-filters">
          <PrimeTabs v-model:value="statusFilter">
            <PrimeTabList>
              <PrimeTab
                v-for="opt in statusFilterOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </PrimeTab>
            </PrimeTabList>
          </PrimeTabs>
        </div>
        <PrimeDataTable
          :value="sortedFilteredConfigs"
          dataKey="file.name"
          size="small"
          rowHover
          :pt="multiFileTablePt"
          :rowGroupMode="multiCategoryMode ? 'subheader' : undefined"
          :groupRowsBy="multiCategoryMode ? getCategoryGroupKey : undefined"
          :expandableRowGroups="multiCategoryMode"
          v-model:expandedRowGroups="expandedGroups"
        >
          <template v-if="multiCategoryMode" #groupheader="{ data }">
            <div class="category-group-header">
              <span>{{ getCategoryGroupLabel(data) }}</span>
              <span class="category-group-count">
                ({{
                  sortedFilteredConfigs.filter(
                    (fc) =>
                      getCategoryGroupKey(fc) === getCategoryGroupKey(data),
                  ).length
                }})
              </span>
            </div>
          </template>
          <PrimeColumn
            :header="t('classificationPlan.multiFile.fileName')"
            style="min-width: 12rem"
          >
            <template #body="{ data }">
              <span :title="data.file.name">{{ data.file.name }}</span>
            </template>
          </PrimeColumn>
          <PrimeColumn
            :header="t('classificationPlan.nature')"
            style="width: 10rem"
          >
            <template #body="{ data }">
              <PrimeTag
                v-if="data.natureCode && data.natureCode !== NO_NATURE_CODE"
                :value="getNatureName(data.natureCode) || data.natureCode"
              />
              <span v-else class="inline-field-na">—</span>
            </template>
          </PrimeColumn>
          <PrimeColumn
            :header="t('classificationPlan.multiFile.metadataBtn')"
            :style="{ minWidth: hasAnyInlineFile ? '20rem' : '8rem' }"
          >
            <template #body="{ data }">
              <!-- No nature -->
              <span
                v-if="!data.natureCode || data.natureCode === NO_NATURE_CODE"
                class="inline-field-na"
                >—</span
              >

              <!-- Inline mode: ≤3 fields -->
              <div
                v-else-if="isInlineMode(data)"
                class="inline-metadata"
                :class="['inline-metadata--' + inlineMetadataLayout]"
              >
                <div
                  v-for="field in getFieldsForNatureCode(data.natureCode)"
                  :key="field.name"
                  class="inline-metadata-field"
                >
                  <label class="inline-metadata-label">
                    {{ field.description }}
                    <span
                      v-if="
                        getRequiredFieldNamesForNature(data.natureCode).has(
                          field.name,
                        )
                      "
                      class="inline-metadata-required"
                      >*</span
                    >
                    <i
                      v-if="getExtractedFieldsForFc(data).has(field.name)"
                      class="ri-sparkling-line auto-extracted-icon"
                      v-tooltip.top="t('classificationPlan.autoExtracted')"
                    />
                  </label>
                  <PrimeDatePicker
                    v-if="field.input === 'date'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeDatePicker
                    v-else-if="field.input === 'datetime'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    showTime
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeInputNumber
                    v-else-if="field.type === 'number'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    :min="field.min"
                    :max="field.max"
                    :placeholder="
                      field.default != null ? String(field.default) : undefined
                    "
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeCheckbox
                    v-else-if="field.input === 'boolean'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    binary
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeSelect
                    v-else-if="field.input === 'list'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    :options="field.values || []"
                    :placeholder="field.description"
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeSelect
                    v-else-if="field.input === 'search'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    :options="getSearchOptions(field.name)"
                    :loading="isSearchLoading(field.name)"
                    :placeholder="t('classificationPlan.searchPlaceholder')"
                    filter
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                    @focus="loadSearchOptions(field)"
                  />
                  <PrimeSelectButton
                    v-else-if="field.input === 'select'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    :options="field.values || []"
                    size="small"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeSelect
                    v-else-if="field.input === 'dictionary'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    :options="getDictionaryEntries(field.source)"
                    option-label="value"
                    option-value="key"
                    :placeholder="field.description"
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeSelect
                    v-else-if="field.input === 'lexicon'"
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    :options="getLexiconValues(field.source)"
                    :placeholder="field.description"
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeSelect
                    v-else-if="
                      field.input === 'parent' &&
                      parentFieldOptions[field.name]?.length > 0 &&
                      !data.metadata[field.name]
                    "
                    :modelValue="data.metadata[field.name]"
                    :options="parentFieldOptions[field.name]"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="t('classificationPlan.selectParentValue')"
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <PrimeInputText
                    v-else-if="field.input === 'parent'"
                    :modelValue="data.metadata[field.name]"
                    disabled
                    size="small"
                    class="w-full"
                  />
                  <PrimeInputText
                    v-else
                    :modelValue="data.metadata[field.name]"
                    :disabled="preFilledFields.has(field.name)"
                    :pattern="field.regex"
                    :placeholder="
                      field.default != null && field.default !== ''
                        ? String(field.default)
                        : undefined
                    "
                    size="small"
                    class="w-full"
                    @update:modelValue="
                      updateInlineField(data, field.name, $event)
                    "
                  />
                  <small
                    v-if="field.default != null && field.default !== ''"
                    class="inline-metadata-default"
                  >
                    {{ t("classificationPlan.defaultValue") }}:
                    {{ field.default }}
                  </small>
                  <small
                    v-if="
                      field.input === 'parent' &&
                      !data.metadata[field.name] &&
                      !parentFieldOptions[field.name]?.length
                    "
                    class="p-error"
                  >
                    {{ t("classificationPlan.parentFieldNotFound") }}
                  </small>
                </div>
              </div>

              <!-- Popup mode: >3 fields, green button when complete -->
              <PrimeButton
                v-else
                :label="metadataButtonLabel(getOriginalIndex(data))"
                :icon="
                  isFileComplete(getOriginalIndex(data))
                    ? 'ri-check-line'
                    : 'ri-edit-line'
                "
                :severity="metadataButtonSeverity(getOriginalIndex(data))"
                text
                size="small"
                @click="openMetadataDialog(getOriginalIndex(data))"
              />
            </template>
          </PrimeColumn>
          <PrimeColumn
            v-if="isFullView"
            :header="t('classificationPlan.multiFile.status')"
            style="width: 7rem"
          >
            <template #body="{ data }">
              <PrimeTag
                :value="
                  isFileComplete(getOriginalIndex(data))
                    ? t('classificationPlan.multiFile.complete')
                    : t('classificationPlan.multiFile.incomplete')
                "
                :severity="
                  isFileComplete(getOriginalIndex(data)) ? 'success' : 'warn'
                "
              />
            </template>
          </PrimeColumn>
          <PrimeColumn style="width: 3rem">
            <template #body="{ data }">
              <PrimeButton
                icon="ri-close-line"
                text
                rounded
                severity="danger"
                size="small"
                @click="removeFile(getOriginalIndex(data))"
              />
            </template>
          </PrimeColumn>
        </PrimeDataTable>
      </div>
    </div>

    <!-- ═══════════ STEP: Summary ═══════════ -->
    <div v-if="!isLoading && currentStep === summaryStep">
      <p>{{ t("classificationPlan.summary.verifyBeforeDeposit") }}</p>

      <PrimePanel
        v-for="(fc, index) in fileConfigs"
        :key="index"
        toggleable
        :collapsed="fileConfigs.length > 1"
        class="summary-file-panel"
      >
        <template #header>
          <div class="summary-panel-header">
            <span class="summary-panel-filename">{{ fc.file.name }}</span>
            <template
              v-if="
                !noNature &&
                fc.natureCode !== NO_NATURE_CODE &&
                computeFileRename(index) !== fc.file.name
              "
            >
              <i class="ri-arrow-right-line"></i>
              <strong>{{ computeFileRename(index) }}</strong>
            </template>
            <PrimeTag
              v-if="!noNature && fc.natureCode !== NO_NATURE_CODE"
              :value="
                isFileComplete(index)
                  ? t('classificationPlan.multiFile.complete')
                  : t('classificationPlan.multiFile.incomplete')
              "
              :severity="isFileComplete(index) ? 'success' : 'warn'"
            />
          </div>
        </template>

        <!-- General info -->
        <div class="summary-section">
          <h5>{{ t("classificationPlan.summary.generalInfo") }}</h5>
          <p>
            <strong>{{ t("classificationPlan.plan") }} :</strong>
            {{ selectedPlanName }}
          </p>
          <p>
            <strong>{{ t("classificationPlan.nature") }} :</strong>
            {{
              noNature || fc.natureCode === NO_NATURE_CODE
                ? t("classificationPlan.noNature")
                : getNatureName(fc.natureCode) || "—"
            }}
          </p>
        </div>

        <PrimeDivider />

        <!-- Metadata (hidden for noNature) -->
        <template v-if="!noNature && fc.natureCode !== NO_NATURE_CODE">
          <div class="summary-section">
            <h5>{{ t("classificationPlan.summary.metadataToSend") }}</h5>
            <ul class="summary-metadata-list">
              <li v-for="(value, key) in computeFileMetadata(index)" :key="key">
                <strong>{{ key }} :</strong> {{ value }}
              </li>
            </ul>
          </div>
          <PrimeDivider />
        </template>

        <!-- Movement (requalify mode) -->
        <div v-if="isRequalifyMode" class="summary-section">
          <h5>{{ t("classificationPlan.summary.movement") }}</h5>
          <p>
            <strong
              >{{ t("classificationPlan.requalify.summaryFrom") }} :</strong
            >
            <code>{{ displayCurrentPath }}</code>
          </p>
          <PrimeDivider />
          <p>
            <strong>{{ t("classificationPlan.requalify.summaryTo") }} :</strong>
            <code>{{ computeDestinationPath(index) }}</code>
          </p>
          <p>
            <strong>{{ t("classificationPlan.summary.document") }} :</strong>
            {{ fc.file.name }}
            <template v-if="computeFileRename(index) !== fc.file.name">
              <i class="ri-arrow-right-line arrow-icon"></i>
              <strong>{{ computeFileRename(index) }}</strong>
            </template>
          </p>
        </div>

        <!-- File classification (upload mode) -->
        <div v-else class="summary-section">
          <h5>{{ t("classificationPlan.summary.fileClassification") }}</h5>
          <p v-if="noNature || fc.natureCode === NO_NATURE_CODE">
            <strong
              >{{ t("classificationPlan.summary.destinationFolder") }} :</strong
            >
            <code>{{ noNatureResolvedPath || "—" }}</code>
          </p>
          <p v-else>
            <strong
              >{{ t("classificationPlan.summary.destinationFolder") }} :</strong
            >
            <code>{{ computeDestinationPath(index) }}</code>
          </p>
        </div>
      </PrimePanel>
    </div>

    <!-- ═══════════ Metadata sub-dialog (multi-file) ═══════════ -->
    <PrimeDialog
      v-model:visible="showMetadataDialog"
      :header="
        t('classificationPlan.multiFile.metadataTitle', {
          fileName: metadataDialogFileName,
        })
      "
      modal
      :draggable="false"
      class="metadata-sub-dialog"
      :style="{ width: 'min(50rem, 90vw)', maxHeight: '90vh' }"
    >
      <!-- The activeFileIndex is set to the dialog target, so the composable outputs are correct -->
      <MetadataFormFields
        v-if="showMetadataDialog && metadataDialogIndex !== null"
        :fields="currentFields"
        :modelValue="activeMetadata"
        :requiredFields="requiredFields"
        :disabledFields="preFilledFields"
        :autoExtractedFields="activeAutoExtractedFields"
        :validationErrors="validationErrors"
        :getSearchOptions="getSearchOptions"
        :isSearchLoading="isSearchLoading"
        :dictionaries="dictionaries"
        :lexicons="lexicons"
        :parentFieldOptions="parentFieldOptions"
        @update:modelValue="activeMetadata = $event"
        @load-search="loadSearchOptions($event)"
        @validate="debounceValidate($event, activeMetadata[$event.name])"
      />
      <template #footer>
        <PrimeButton
          :label="t('validate')"
          icon="ri-check-line"
          @click="closeMetadataDialog"
        />
      </template>
    </PrimeDialog>

    <!-- Footer (dialog mode) -->
    <template v-if="!inline" #footer>
      <PrimeButton
        :label="t('classificationPlan.buttons.cancel')"
        icon="ri-close-line"
        text
        @click="isVisible = false"
      />
      <PrimeButton
        v-if="currentStep > firstStep"
        :label="t('classificationPlan.buttons.previous')"
        icon="ri-arrow-left-line"
        text
        @click="prevStep"
      />
      <PrimeButton
        v-if="currentStep < configStep"
        :label="t('classificationPlan.buttons.next')"
        icon="ri-arrow-right-line"
        icon-pos="right"
        :disabled="!canGoNext"
        @click="nextStep"
      />
      <PrimeButton
        v-if="currentStep === configStep"
        :label="t('classificationPlan.stepper.summary')"
        icon="ri-file-list-line"
        text
        :disabled="!canGoNext"
        @click="nextStep"
      />
      <PrimeButton
        v-if="currentStep === configStep || currentStep === summaryStep"
        :label="
          isDepositing
            ? t('classificationPlan.buttons.depositing')
            : isRequalifyMode
              ? t('classificationPlan.requalify.title')
              : isCreateMode
                ? t('classificationPlan.buttons.acceptAndCreate')
                : t('classificationPlan.buttons.deposit')
        "
        icon="ri-upload-2-line"
        :disabled="!canDeposit || isDepositing"
        @click="doDeposit"
      />
    </template>

    <!-- Footer (inline mode) -->
    <div v-if="inline" class="upload-inline-footer">
      <PrimeButton
        v-if="currentStep > firstStep"
        :label="t('classificationPlan.buttons.previous')"
        icon="ri-arrow-left-line"
        text
        @click="prevStep"
      />
      <PrimeButton
        v-if="currentStep < configStep"
        :label="t('classificationPlan.buttons.next')"
        icon="ri-arrow-right-line"
        icon-pos="right"
        :disabled="!canGoNext"
        @click="nextStep"
      />
      <PrimeButton
        v-if="currentStep === configStep"
        :label="t('classificationPlan.stepper.summary')"
        icon="ri-file-list-line"
        text
        :disabled="!canGoNext"
        @click="nextStep"
      />
      <PrimeButton
        v-if="currentStep === configStep || currentStep === summaryStep"
        :label="
          isDepositing
            ? t('classificationPlan.buttons.depositing')
            : isRequalifyMode
              ? t('classificationPlan.requalify.title')
              : isCreateMode
                ? t('classificationPlan.buttons.acceptAndCreate')
                : t('classificationPlan.buttons.deposit')
        "
        icon="ri-upload-2-line"
        :disabled="!canDeposit || isDepositing"
        @click="doDeposit"
      />
    </div>
  </component>
</template>

<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useToast } from "primevue/usetoast";
import classificationService from "@/services/ClassificationService.js";
import classificationFileService from "@/services/ClassificationFileService.js";
import { nodesApi, searchApi } from "@/AlfrescoApi.js";
import { useNavigationStore } from "@/stores/navigation.js";
import { useUploadStore } from "@/stores/upload.js";
import { useConfigStore } from "@/stores/config.js";
import { useClassificationPlan } from "@/composables/useClassificationPlan.js";
import {
  resolveTemplate,
  extractVariables,
  deduplicateFileNames,
  detectNatureFromFilename,
} from "@/utils/classificationPlanUtils.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import translationService from "@/services/TranslationService.js";
import { useI18n } from "vue-i18n";
import NatureSelector from "./NatureSelector.vue";
import MetadataFormFields from "./MetadataFormFields.vue";

import Dialog from "primevue/dialog";
import Button from "primevue/button";
import FileUpload from "primevue/fileupload";
import Panel from "primevue/panel";
import Tag from "primevue/tag";
import Stepper from "primevue/stepper";
import StepList from "primevue/steplist";
import Step from "primevue/step";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Select from "primevue/select";
import SelectButton from "primevue/selectbutton";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import ProgressBar from "primevue/progressbar";
import Divider from "primevue/divider";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import Checkbox from "primevue/checkbox";
import DatePicker from "primevue/datepicker";

const PrimeDialog = Dialog;
const PrimeButton = Button;
const PrimeFileUpload = FileUpload;
const PrimePanel = Panel;
const PrimeTag = Tag;
const PrimeStepper = Stepper;
const PrimeStepList = StepList;
const PrimeStep = Step;
const PrimeDataTable = DataTable;
const PrimeColumn = Column;
const PrimeSelect = Select;
const PrimeSelectButton = SelectButton;
const PrimeTabs = Tabs;
const PrimeTabList = TabList;
const PrimeTab = Tab;
const PrimeProgressBar = ProgressBar;
const PrimeDivider = Divider;
const PrimeInputNumber = InputNumber;
const PrimeInputText = InputText;
const PrimeCheckbox = Checkbox;
const PrimeDatePicker = DatePicker;

const props = defineProps({
  visible: { type: Boolean, default: false },
  currentNode: { type: Object, default: null },
  forcedPlanName: { type: String, default: null },
  filesToUpload: { type: Array, default: () => [] },
  defaultDestinationNodeId: { type: String, default: null },
  // Create file mode: { templateNodeId, fileName, fileExtension, fileType }
  createFileMode: { type: Object, default: null },
  forceSingleCategory: { type: Boolean, default: false },
  defaultCategoryId: { type: String, default: null },
  duplicateNameStrategy: { type: String, default: null },
  inline: { type: Boolean, default: false },
});

const emit = defineEmits([
  "update:visible",
  "close",
  "file-created",
  "upload-complete",
]);

const { t } = useI18n();
const toast = useToast();
const navigationStore = useNavigationStore();
const configStore = useConfigStore();
const allowNoNature = computed(
  () => configStore.CLASSIFICATION_PLAN_ALLOW_NO_NATURE === true,
);

const isVisible = computed({
  get: () => props.inline || props.visible,
  set: (val) => {
    if (!props.inline) {
      emit("update:visible", val);
    }
  },
});

// ─── Mode computed ───
const isRequalifyMode = computed(() => !!props.currentNode);
const isForcedPlan = computed(() => !!props.forcedPlanName);
const isCreateMode = computed(() => !!props.createFileMode);

// ─── Dynamic step numbers ───
const planStep = computed(() => 1);
const skipFilesStep = computed(
  () => isRequalifyMode.value || isCreateMode.value,
);
const filesStep = computed(() => {
  if (skipFilesStep.value) {
    return -1;
  }
  return isForcedPlan.value ? 1 : 2;
});
const natureStep = computed(() => {
  if (skipFilesStep.value) {
    return isForcedPlan.value ? 1 : 2;
  }
  return isForcedPlan.value ? 2 : 3;
});
const configStep = computed(() => {
  if (skipFilesStep.value) {
    return isForcedPlan.value ? 2 : 3;
  }
  return isForcedPlan.value ? 3 : 4;
});
const summaryStep = computed(() => {
  if (skipFilesStep.value) {
    return isForcedPlan.value ? 3 : 4;
  }
  return isForcedPlan.value ? 4 : 5;
});
const firstStep = computed(() => 1);

const visibleSteps = computed(() => {
  const steps = [];
  if (!isForcedPlan.value) {
    steps.push({
      value: planStep.value,
      label: t("classificationPlan.steps.selectPlan"),
    });
  }
  if (!skipFilesStep.value) {
    steps.push({
      value: filesStep.value,
      label: t("classificationPlan.stepper.files"),
    });
  }
  steps.push({
    value: natureStep.value,
    label: t("classificationPlan.stepper.nature"),
  });
  steps.push({
    value: configStep.value,
    label: t("classificationPlan.stepper.metadata"),
  });
  steps.push({
    value: summaryStep.value,
    label: t("classificationPlan.stepper.summary"),
  });
  return steps;
});

// ─── State ───
const currentStep = ref(1);
const NO_NATURE_CODE = "__NO_NATURE__";

const isLoading = ref(false);
const isDepositing = ref(false);
const planContent = ref(null);
const workspaceSiteId = ref(null);
const noNature = ref(false);
const noNatureResolvedPath = ref(null);
const autoDetectedAllMatch = ref(false);
const filenameExtractedFields = ref(new Map());

// Plan selection (when not forced)
const planFiles = ref([]);
const selectedPlanName = ref(null);

// File configs: each entry = { file, categoryId, natureCode, metadata: {} }
const fileConfigs = ref([]);

// Category mode
const multiCategoryMode = ref(!props.forceSingleCategory);
const globalCategoryId = ref(null);

// Bulk selection
const selectedFiles = ref([]);
const bulkCategoryId = ref(null);
const bulkNatureCode = ref(null);
const statusFilter = ref("all");
const expandedGroups = ref([]);

// Active file index — the composable always reflects THIS file's data
const activeFileIndex = ref(0);

// Metadata sub-dialog
const showMetadataDialog = ref(false);
const metadataDialogIndex = ref(null);

// Parent context detection
const parentFolderContext = ref(null);
const preFilledFields = ref(new Set());
const parentFieldOptions = ref({});

// ─── Reactive bridge: composable reads/writes the ACTIVE file ───
const activeMetadata = computed({
  get() {
    const fc = fileConfigs.value[activeFileIndex.value];
    return fc ? fc.metadata : {};
  },
  set(val) {
    const fc = fileConfigs.value[activeFileIndex.value];
    if (fc) {
      fc.metadata = val;
    }
  },
});

const activeNatureCode = computed(() => {
  const fc = fileConfigs.value[activeFileIndex.value];
  return fc ? fc.natureCode : null;
});

const activeSelectedNature = computed(() => {
  if (!activeNatureCode.value) {
    return null;
  }
  return (
    natureOptions.value.find((n) => n.code === activeNatureCode.value) || null
  );
});

const activeAutoExtractedFields = computed(() => {
  return filenameExtractedFields.value.get(activeFileIndex.value) || new Set();
});

const emptySet = new Set();
function getExtractedFieldsForFc(fc) {
  const index = fileConfigs.value.indexOf(fc);
  if (index === -1) {
    return emptySet;
  }
  return filenameExtractedFields.value.get(index) || emptySet;
}

const destinationNodeName = ref(null);

// ─── Single composable instance created during setup ───
const {
  currentFields,
  requiredFields,
  summaryPathFull,
  summaryDestinationPath,
  summaryMetadata,
  resolveTemplateReactive,
  getSearchOptions,
  isSearchLoading,
  loadSearchOptions,
  validationErrors,
  debounceValidate,
  dictionaries,
  lexicons,
  resetClassification,
} = useClassificationPlan({
  planContent,
  metadataValues: activeMetadata,
  selectedNature: activeSelectedNature,
  destinationNodeName,
});

const getDictionaryEntries = (sourceId) => {
  const dict = dictionaries.value.find((d) => d.id === sourceId);
  return dict ? dict.entries || [] : [];
};

const getLexiconValues = (sourceId) => {
  const lex = lexicons.value.find((l) => l.id === sourceId);
  return lex ? lex.values || [] : [];
};

// ─── Inline metadata layout toggle ───
const inlineMetadataLayout = ref("row");
const hasAnyInlineFile = computed(() => {
  return fileConfigs.value.some((fc) => isInlineMode(fc));
});

const hasAnyNatureSelected = computed(() =>
  fileConfigs.value.some((fc) => fc.natureCode != null),
);

// ─── Full view threshold ───
const fullViewThreshold = ref(
  configStore.CLASSIFICATION_PLAN_FULL_VIEW_THRESHOLD || 5,
);
const isFullView = computed(
  () => fileConfigs.value.length >= fullViewThreshold.value,
);

// ─── Computed ───
const isMultiFile = computed(() => fileConfigs.value.length > 1);

const multiFileTablePt = computed(() => ({
  bodyRow: ({ context }) => ({
    style: context.selected
      ? "background: color-mix(in srgb, var(--p-primary-color) 12%, var(--p-content-background))"
      : undefined,
  }),
}));

const natureOptions = computed(() => planContent.value?.NATURES || []);

const importCategories = computed(() =>
  (planContent.value?.VIEW_CATEGORIES || []).filter(
    (cat) => cat.display && cat.display.includes("import"),
  ),
);

function getCategoryGroupKey(fc) {
  return fc.categoryId || "__unassigned__";
}

function getCategoryGroupLabel(fc) {
  if (!fc.categoryId) {
    return t("classificationPlan.multiFile.unassigned");
  }
  const cat = importCategories.value.find((c) => c.id === fc.categoryId);
  return cat?.label || fc.categoryId;
}

function getOriginalIndex(data) {
  return fileConfigs.value.indexOf(data);
}

const sortedFilteredConfigs = computed(() => {
  // Filter by status
  let items = fileConfigs.value.filter((fc, i) => {
    if (statusFilter.value === "incomplete") {
      return !isFileComplete(i);
    }
    if (statusFilter.value === "complete") {
      return isFileComplete(i);
    }
    return true;
  });

  // Sort: unassigned first, then by category order in importCategories
  const grouped = new Map();
  for (const fc of items) {
    const key = getCategoryGroupKey(fc);
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key).push(fc);
  }

  const result = [];
  if (grouped.has("__unassigned__")) {
    result.push(...grouped.get("__unassigned__"));
    grouped.delete("__unassigned__");
  }
  for (const cat of importCategories.value) {
    if (grouped.has(cat.id)) {
      result.push(...grouped.get(cat.id));
      grouped.delete(cat.id);
    }
  }
  for (const remaining of grouped.values()) {
    result.push(...remaining);
  }
  return result;
});

// Auto-expand all category groups when the underlying file list changes
watch(
  () => fileConfigs.value.map(getCategoryGroupKey).join(","),
  () => {
    const keys = [
      ...new Set(sortedFilteredConfigs.value.map(getCategoryGroupKey)),
    ];
    expandedGroups.value = keys;
  },
  { immediate: true },
);

watch(isFullView, (full) => {
  if (!full) {
    statusFilter.value = "all";
  }
});

watch(multiCategoryMode, (isMulti) => {
  if (!isMulti) {
    const cats = new Set(
      fileConfigs.value.map((fc) => fc.categoryId).filter(Boolean),
    );
    globalCategoryId.value = cats.size === 1 ? [...cats][0] : null;
    if (globalCategoryId.value) {
      for (const fc of fileConfigs.value) {
        fc.categoryId = globalCategoryId.value;
      }
    }
  }
});

const incompleteCount = computed(
  () => fileConfigs.value.filter((_, i) => !isFileComplete(i)).length,
);
const completeCount = computed(
  () => fileConfigs.value.filter((_, i) => isFileComplete(i)).length,
);

const statusFilterOptions = computed(() => [
  {
    label: t("classificationPlan.multiFile.filterAll", {
      count: fileConfigs.value.length,
    }),
    value: "all",
  },
  {
    label: t("classificationPlan.multiFile.filterIncomplete", {
      count: incompleteCount.value,
    }),
    value: "incomplete",
  },
  {
    label: t("classificationPlan.multiFile.filterComplete", {
      count: completeCount.value,
    }),
    value: "complete",
  },
]);

const requalifyCategories = computed(() =>
  (planContent.value?.VIEW_CATEGORIES || []).filter(
    (cat) => cat.display && cat.display.includes("requalify"),
  ),
);

const activeCategories = computed(() =>
  isRequalifyMode.value ? requalifyCategories.value : importCategories.value,
);

const categoryOptions = computed(() => {
  const options = [
    { label: t("classificationPlan.multiFile.allCategories"), value: null },
  ];
  importCategories.value.forEach((cat) => {
    options.push({ label: cat.label, value: cat.id });
  });
  return options;
});

const bulkCategoryOptions = computed(() => {
  return importCategories.value.map((cat) => ({
    label: cat.label,
    value: cat.id,
  }));
});

const bulkNatureOptions = computed(() => {
  if (!multiCategoryMode.value && globalCategoryId.value) {
    return getNaturesForCategory(globalCategoryId.value);
  }
  return getNaturesForCategory(null);
});

const categoryModeOptions = computed(() => [
  { label: t("classificationPlan.multiFile.modeMultiCategory"), value: true },
  { label: t("classificationPlan.multiFile.modeMonoCategory"), value: false },
]);

const stepTitle = computed(() => {
  if (currentStep.value === planStep.value && !isForcedPlan.value) {
    return t("classificationPlan.steps.selectPlan");
  }
  if (currentStep.value === filesStep.value && !isRequalifyMode.value) {
    return t("classificationPlan.steps.selectFiles");
  }
  if (currentStep.value === natureStep.value) {
    return t("classificationPlan.steps.selectNature");
  }
  if (currentStep.value === configStep.value) {
    return isMultiFile.value
      ? t("classificationPlan.multiFile.configMultiple")
      : t("classificationPlan.multiFile.configSingle");
  }
  if (currentStep.value === summaryStep.value) {
    return t("classificationPlan.steps.summary");
  }
  return "";
});

const canGoNext = computed(() => {
  // Plan step
  if (currentStep.value === planStep.value && !isForcedPlan.value) {
    return !!selectedPlanName.value;
  }
  // Files step
  if (currentStep.value === filesStep.value && !isRequalifyMode.value) {
    return fileConfigs.value.length > 0;
  }
  // Nature step
  if (currentStep.value === natureStep.value) {
    if (noNature.value) {
      return true;
    }
    return (
      fileConfigs.value.length > 0 &&
      fileConfigs.value.every((fc) => !!fc.natureCode)
    );
  }
  // Config step
  if (currentStep.value === configStep.value) {
    return fileConfigs.value.every((_, i) => isFileComplete(i));
  }
  return false;
});

const canDeposit = computed(() => {
  if (noNature.value) {
    return !isRequalifyMode.value && fileConfigs.value.length > 0;
  }
  return (
    fileConfigs.value.length > 0 &&
    fileConfigs.value.every((_, i) => isFileComplete(i))
  );
});

const metadataDialogFileName = computed(() => {
  if (
    metadataDialogIndex.value !== null &&
    fileConfigs.value[metadataDialogIndex.value]
  ) {
    return fileConfigs.value[metadataDialogIndex.value].file.name;
  }
  return "";
});

const getActiveNatureName = computed(() => {
  return activeSelectedNature.value?.name || activeNatureCode.value || "";
});

// Requalify: display current path
const displayCurrentPath = computed(() => {
  if (!props.currentNode) {
    return "";
  }
  const pathName = props.currentNode.path?.name;
  if (!pathName) {
    return props.currentNode.name;
  }
  if (destinationNodeName.value) {
    const parts = pathName.split("/");
    const destIndex = parts.indexOf(destinationNodeName.value);
    if (destIndex !== -1) {
      const relativeParts = parts.slice(destIndex);
      relativeParts.push(props.currentNode.name);
      return relativeParts.join("/");
    }
  }
  const parts = pathName.split("/");
  const docLibIndex = parts.indexOf("documentLibrary");
  if (docLibIndex !== -1) {
    const relativeParts = parts.slice(docLibIndex + 1);
    relativeParts.push(props.currentNode.name);
    return relativeParts.join("/");
  }
  return pathName + "/" + props.currentNode.name;
});

// ─── Helpers ───

function formatFileSize(bytes) {
  if (!bytes) {
    return "-";
  }
  if (translationService?.convertBytesToSize) {
    return translationService.convertBytesToSize(bytes) || "-";
  }
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let size = bytes;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i++;
  }
  return `${size.toFixed(1)} ${units[i]}`;
}

function getNatureName(code) {
  if (!code) {
    return null;
  }
  const nature = natureOptions.value.find((n) => n.code === code);
  return nature?.name || code;
}

function getNaturesForCategory(categoryId) {
  let natures;
  if (!categoryId) {
    natures = natureOptions.value;
  } else {
    natures = natureOptions.value.filter(
      (n) => n.categories && n.categories.includes(categoryId),
    );
  }
  // Add "no nature" option at the top when allowed in multi-file mode
  if (allowNoNature.value && !isRequalifyMode.value && !isCreateMode.value) {
    return [
      { code: NO_NATURE_CODE, name: t("classificationPlan.noNature") },
      ...natures,
    ];
  }
  return natures;
}

/**
 * Compute the renamed file name for a given file config.
 */
function computeFileRename(index) {
  const fc = fileConfigs.value[index];
  if (!fc) {
    return null;
  }

  const nature = natureOptions.value.find((n) => n.code === fc.natureCode);
  const nameTemplate = nature?.metadata?.["cm:name"]?.template;
  if (!nameTemplate) {
    return fc.file.name;
  }

  const fields = planContent.value?.FIELDS || [];
  let resolved = resolveTemplate(nameTemplate, fc.metadata, fields);

  // Preserve original file extension
  if (resolved) {
    const originalName = fc.file.name;
    const dotIndex = originalName.lastIndexOf(".");
    const extension = dotIndex > 0 ? originalName.substring(dotIndex) : "";
    resolved = resolved + extension;
  }

  return resolved || fc.file.name;
}

/**
 * Compute resolved metadata for a given file (for summary display).
 */
function computeFileMetadata(index) {
  const fc = fileConfigs.value[index];
  if (!fc?.natureCode) {
    return {};
  }

  const nature = natureOptions.value.find((n) => n.code === fc.natureCode);
  if (!nature?.metadata) {
    // Even without metadata config, store the nature code if configured
    const prop = configStore.CLASSIFICATION_PLAN_NATURE_CODE_PROPERTY;
    return prop ? { [prop]: fc.natureCode } : {};
  }

  const fields = planContent.value?.FIELDS || [];

  // Build parentProperties from the folder hierarchy (same as composable)
  const parentProperties = {};
  const pathFull = computePathFull(index);
  pathFull.forEach((folder) => {
    if (folder.properties) {
      Object.assign(parentProperties, folder.properties);
    }
  });

  const result = {};

  // Get original file extension for cm:name preservation
  const originalName = fc.file.name;
  const dotIdx = originalName.lastIndexOf(".");
  const fileExtension = dotIdx > 0 ? originalName.substring(dotIdx) : "";

  for (const [key, valueObj] of Object.entries(nature.metadata)) {
    const type = Object.keys(valueObj).find((k) => k !== "required");
    const value = valueObj[type];

    switch (type) {
      case "value":
        result[key] =
          key === "cm:name" && fileExtension ? value + fileExtension : value;
        break;
      case "field": {
        let fieldVal = fc.metadata[value];
        const field = fields.find((f) => f.name === value);
        if (fieldVal === undefined || fieldVal === null || fieldVal === "") {
          if (field?.optional === true) {
            if (field?.default !== undefined && field?.default !== null) {
              fieldVal = field.default;
            } else {
              continue; // optional without default → skip property
            }
          }
        }
        result[key] =
          key === "cm:name" && fileExtension
            ? fieldVal + fileExtension
            : fieldVal;
        break;
      }
      case "template": {
        const resolved = resolveTemplate(value, fc.metadata, fields);
        result[key] =
          key === "cm:name" && fileExtension && resolved
            ? resolved + fileExtension
            : resolved;
        break;
      }
      case "parent":
        if (parentProperties[key]) {
          result[key] = parentProperties[key];
        }
        continue;
      default:
        continue;
    }
  }

  // If config declares a property for storing the nature code, add it
  const natureCodeProperty =
    configStore.CLASSIFICATION_PLAN_NATURE_CODE_PROPERTY;
  if (natureCodeProperty) {
    result[natureCodeProperty] = fc.natureCode;
  }

  return result;
}

/**
 * Compute the full path (with types/properties) for a file, matching summaryPathFull logic.
 */
function computePathFull(index) {
  const fc = fileConfigs.value[index];
  if (!fc?.natureCode) {
    return [];
  }

  const nature = natureOptions.value.find((n) => n.code === fc.natureCode);
  if (!nature?.emplacement || !planContent.value?.EMPLACEMENT) {
    return [];
  }

  const fields = planContent.value?.FIELDS || [];

  return nature.emplacement.map((code) => {
    const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
    if (!emp) {
      return { name: `[${code}]` };
    }

    const folderNode = {};
    if (emp.type) {
      folderNode.type = emp.type;
    }

    const properties = {};
    let folderName = null;

    if (emp.metadata) {
      for (const [key, valueObj] of Object.entries(emp.metadata)) {
        const type = Object.keys(valueObj).find((k) => k !== "required");
        const value = valueObj[type];

        switch (type) {
          case "value":
            properties[key] = value;
            break;
          case "field": {
            let fieldValue = fc.metadata[value];
            const field = fields.find((f) => f.name === value);
            if (
              fieldValue === undefined ||
              fieldValue === null ||
              fieldValue === ""
            ) {
              if (field?.optional === true) {
                if (field?.default !== undefined && field?.default !== null) {
                  fieldValue = field.default;
                } else {
                  break; // optional without default → skip property
                }
              }
            }
            properties[key] = fieldValue;
            break;
          }
          case "template":
            properties[key] = resolveTemplate(value, fc.metadata, fields);
            break;
          case "parent":
            break;
        }

        if (key === "cm:name" && type !== "parent") {
          switch (type) {
            case "value":
              folderName = value;
              break;
            case "field":
              folderName = fc.metadata[value] || null;
              break;
            case "template":
              folderName = resolveTemplate(value, fc.metadata, fields);
              break;
          }
        }
      }
    }

    if (Object.keys(properties).length > 0) {
      folderNode.properties = properties;
    }
    folderNode.name = folderName || emp.name || `[${code}]`;
    return folderNode;
  });
}

/**
 * Check if a file config is complete WITHOUT the composable.
 */
function isFileComplete(index) {
  const fc = fileConfigs.value[index];
  if (!fc || !fc.natureCode) {
    return false;
  }

  // "No nature" is always complete
  if (fc.natureCode === NO_NATURE_CODE) {
    return true;
  }

  const nature = natureOptions.value.find((n) => n.code === fc.natureCode);
  if (!nature) {
    return false;
  }

  const requiredFieldNames = new Set();
  if (nature.metadata) {
    for (const [, valueObj] of Object.entries(nature.metadata)) {
      if (valueObj.required && valueObj.field) {
        requiredFieldNames.add(valueObj.field);
      }
      if (valueObj.template) {
        extractVariables(valueObj.template).forEach((v) =>
          requiredFieldNames.add(v),
        );
      }
    }
  }

  if (nature.emplacement && planContent.value?.EMPLACEMENT) {
    for (const code of nature.emplacement) {
      const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
      if (emp?.metadata) {
        for (const [, valueObj] of Object.entries(emp.metadata)) {
          if (valueObj.required && valueObj.field) {
            requiredFieldNames.add(valueObj.field);
          }
          if (valueObj.template) {
            extractVariables(valueObj.template).forEach((v) =>
              requiredFieldNames.add(v),
            );
          }
        }
      }
    }
  }

  const fields = planContent.value?.FIELDS || [];
  const optionalFields = new Set(
    fields.filter((f) => f.optional === true).map((f) => f.name),
  );

  const metadata = fc.metadata || {};
  for (const fieldName of requiredFieldNames) {
    if (optionalFields.has(fieldName)) {
      continue;
    }
    const val = metadata[fieldName];
    if (val === undefined || val === null || val === "") {
      return false;
    }
  }

  return true;
}

// ─── Inline metadata helpers ───

function getFieldsForNatureCode(natureCode) {
  if (!natureCode || !planContent.value?.FIELDS) {
    return [];
  }
  const nature = natureOptions.value.find((n) => n.code === natureCode);
  if (!nature) {
    return [];
  }

  const fieldNameSet = new Set();

  if (nature.metadata) {
    for (const valueObj of Object.values(nature.metadata)) {
      if (valueObj.field) {
        fieldNameSet.add(valueObj.field);
      }
      if (valueObj.template) {
        extractVariables(valueObj.template).forEach((v) => fieldNameSet.add(v));
      }
    }
  }
  if (nature.emplacement && planContent.value.EMPLACEMENT) {
    for (const empCode of nature.emplacement) {
      const emp = planContent.value.EMPLACEMENT.find((e) => e.code === empCode);
      if (emp?.metadata) {
        for (const valueObj of Object.values(emp.metadata)) {
          if (valueObj.field) {
            fieldNameSet.add(valueObj.field);
          }
          if (valueObj.template) {
            extractVariables(valueObj.template).forEach((v) =>
              fieldNameSet.add(v),
            );
          }
        }
      }
    }
  }

  return planContent.value.FIELDS.filter((f) => fieldNameSet.has(f.name));
}

function getRequiredFieldNamesForNature(natureCode) {
  const nature = natureOptions.value.find((n) => n.code === natureCode);
  if (!nature) {
    return new Set();
  }

  const requiredFieldNames = new Set();
  if (nature.metadata) {
    for (const [, valueObj] of Object.entries(nature.metadata)) {
      if (valueObj.required && valueObj.field) {
        requiredFieldNames.add(valueObj.field);
      }
      if (valueObj.template) {
        extractVariables(valueObj.template).forEach((v) =>
          requiredFieldNames.add(v),
        );
      }
    }
  }

  if (nature.emplacement && planContent.value?.EMPLACEMENT) {
    for (const code of nature.emplacement) {
      const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
      if (emp?.metadata) {
        for (const [, valueObj] of Object.entries(emp.metadata)) {
          if (valueObj.required && valueObj.field) {
            requiredFieldNames.add(valueObj.field);
          }
          if (valueObj.template) {
            extractVariables(valueObj.template).forEach((v) =>
              requiredFieldNames.add(v),
            );
          }
        }
      }
    }
  }

  const fields = planContent.value?.FIELDS || [];
  const optionalFields = new Set(
    fields.filter((f) => f.optional === true).map((f) => f.name),
  );
  for (const fieldName of requiredFieldNames) {
    if (optionalFields.has(fieldName)) {
      requiredFieldNames.delete(fieldName);
    }
  }

  return requiredFieldNames;
}

function isInlineMode(fc) {
  if (!fc.natureCode || fc.natureCode === NO_NATURE_CODE) {
    return false;
  }
  const threshold =
    configStore.CLASSIFICATION_PLAN_INLINE_METADATA_THRESHOLD || 3;
  const fields = getFieldsForNatureCode(fc.natureCode);
  return fields.length > 0 && fields.length <= threshold;
}

function filledFieldCount(index) {
  const fc = fileConfigs.value[index];
  if (!fc?.natureCode) {
    return { filled: 0, total: 0 };
  }
  const fields = getFieldsForNatureCode(fc.natureCode);
  const metadata = fc.metadata || {};
  let filled = 0;
  for (const field of fields) {
    const val = metadata[field.name];
    if (val !== undefined && val !== null && val !== "") {
      filled++;
    }
  }
  return { filled, total: fields.length };
}

function metadataButtonLabel(index) {
  const { filled, total } = filledFieldCount(index);
  if (total === 0) {
    return t("classificationPlan.multiFile.metadataBtn");
  }
  return t("classificationPlan.multiFile.metadataProgress", { filled, total });
}

function metadataButtonSeverity(index) {
  const { filled, total } = filledFieldCount(index);
  if (total === 0 || filled === 0) {
    return undefined;
  }
  if (filled >= total) {
    return "success";
  }
  return "warn";
}

function updateInlineField(fc, fieldName, value) {
  fc.metadata = { ...fc.metadata, [fieldName]: value };
}

// ─── Filename nature detection ───

function filterOutParentFields(extractedMetadata) {
  const fields = planContent.value?.FIELDS || [];
  const parentFieldNames = new Set(
    fields.filter((f) => f.input === "parent").map((f) => f.name),
  );
  const filtered = {};
  for (const [key, value] of Object.entries(extractedMetadata)) {
    if (
      !parentFieldNames.has(key) &&
      !preFilledFields.value.has(key) &&
      !parentFolderContext.value?.extractedValues?.[key]
    ) {
      filtered[key] = value;
    }
  }
  return filtered;
}

function retryAutoDetectNature() {
  for (const fc of fileConfigs.value) {
    fc.natureCode = null;
    fc.metadata = {};
  }
  filenameExtractedFields.value = new Map();
  tryAutoDetectNature();
}

function tryAutoDetectNature() {
  if (!configStore.FILENAME_NATURE_DETECTION) {
    return;
  }
  if (fileConfigs.value.length === 0) {
    return;
  }
  if (!planContent.value) {
    return;
  }

  const results = fileConfigs.value.map((fc) =>
    detectNatureFromFilename(
      fc.file.name,
      planContent.value.NATURES,
      planContent.value.FIELDS,
    ),
  );

  const firstDetected = results.find((r) => r !== null);
  if (!firstDetected) {
    return;
  }

  if (props.forceSingleCategory) {
    // Single category mode: use first detected nature to determine the category,
    // then resolve nature + metadata for all files within that category
    const detectedNature = firstDetected.nature;

    // Pre-select the category/phase from the first detected nature
    if (!globalCategoryId.value && detectedNature.categories?.length > 0) {
      const natureCategoryId = detectedNature.categories[0];
      const match = importCategories.value.find(
        (c) => c.id === natureCategoryId,
      );
      if (match) {
        globalCategoryId.value = match.id;
      }
    }

    // Resolve nature + metadata for each file within the selected category
    let allDetected = true;
    for (let i = 0; i < fileConfigs.value.length; i++) {
      const fc = fileConfigs.value[i];
      if (fc.natureCode) {
        continue;
      }

      const detected = results[i];
      if (
        detected &&
        (!globalCategoryId.value ||
          !detected.nature.categories ||
          detected.nature.categories.includes(globalCategoryId.value))
      ) {
        fc.natureCode = detected.nature.code;
        // Inject extracted metadata (excluding parent fields)
        if (detected.extractedMetadata) {
          const filtered = filterOutParentFields(detected.extractedMetadata);
          fc.metadata = { ...fc.metadata, ...filtered };
          const fieldSet = new Set(Object.keys(filtered));
          if (fieldSet.size > 0) {
            filenameExtractedFields.value.set(i, fieldSet);
          }
        }
        // Initialize metadata for this file (resolves parent fields, defaults, etc.)
        initializeMetadataForFile(i);
      } else {
        allDetected = false;
      }
    }

    autoDetectedAllMatch.value = allDetected;
  } else {
    // Multi category mode: set nature and category per file
    let allDetected = true;
    for (let i = 0; i < fileConfigs.value.length; i++) {
      const fc = fileConfigs.value[i];
      if (fc.natureCode) {
        continue;
      }

      const detected = results[i];
      if (detected) {
        fc.natureCode = detected.nature.code;
        // Inject extracted metadata (excluding parent fields)
        if (detected.extractedMetadata) {
          const filtered = filterOutParentFields(detected.extractedMetadata);
          fc.metadata = { ...fc.metadata, ...filtered };
          const fieldSet = new Set(Object.keys(filtered));
          if (fieldSet.size > 0) {
            filenameExtractedFields.value.set(i, fieldSet);
          }
        }
        if (detected.nature.categories?.length > 0) {
          const natureCategoryId = detected.nature.categories[0];
          const match = importCategories.value.find(
            (c) => c.id === natureCategoryId,
          );
          if (match) {
            fc.categoryId = match.id;
          }
        }
      } else {
        allDetected = false;
      }
    }
    autoDetectedAllMatch.value = allDetected;
  }
}

// ─── File management ───

function createFileConfig(file) {
  return {
    file,
    categoryId: null,
    natureCode: null,
    metadata: {},
  };
}

function onFileSelect(event) {
  const existingNames = new Set(fileConfigs.value.map((fc) => fc.file.name));
  const newFiles = event.files.filter((f) => !existingNames.has(f.name));
  newFiles.forEach((f) => {
    fileConfigs.value.push(createFileConfig(f));
  });
  tryAutoDetectNature();
}

function removeFile(index) {
  fileConfigs.value.splice(index, 1);
  // Rebuild filenameExtractedFields with shifted indices
  const newMap = new Map();
  for (const [i, fields] of filenameExtractedFields.value) {
    if (i < index) {
      newMap.set(i, fields);
    } else if (i > index) {
      newMap.set(i - 1, fields);
    }
  }
  filenameExtractedFields.value = newMap;
  if (activeFileIndex.value >= fileConfigs.value.length) {
    activeFileIndex.value = Math.max(0, fileConfigs.value.length - 1);
  }
}

// ─── Nature selection ───

function onSingleNatureSelect(natureCode) {
  activeFileIndex.value = 0;
  fileConfigs.value[0].natureCode = natureCode;
  initializeActiveMetadata();
}

function clearSingleNature() {
  fileConfigs.value[0].natureCode = null;
  fileConfigs.value[0].metadata = {};
  filenameExtractedFields.value = new Map();
  resetClassification();
}

function onSelectNoNature() {
  noNature.value = true;
  fileConfigs.value[0].natureCode = null;
  resolveNoNaturePath();
}

function clearNoNature() {
  noNature.value = false;
  noNatureResolvedPath.value = null;
}

async function resolveNoNaturePath() {
  try {
    const destNodeId = props.defaultDestinationNodeId;
    if (destNodeId) {
      const destResponse = await nodesApi.getNode(destNodeId);
      const displayParts = [destResponse.entry.name];
      const currentNodeId = navigationStore.currentNode?.id;
      if (currentNodeId && currentNodeId !== destNodeId) {
        const currentResponse = await nodesApi.getNode(currentNodeId, {
          include: ["path"],
        });
        const pathElements = currentResponse.entry.path?.elements || [];
        const destIndex = pathElements.findIndex((el) => el.id === destNodeId);
        if (destIndex !== -1) {
          const relativeParts = pathElements
            .slice(destIndex + 1)
            .map((el) => el.name);
          displayParts.push(...relativeParts);
        }
        displayParts.push(currentResponse.entry.name);
      }
      noNatureResolvedPath.value = "/ " + displayParts.join(" / ");
    } else {
      const nodeId = navigationStore.currentNode?.id;
      if (nodeId) {
        const nodeResponse = await nodesApi.getNode(nodeId, {
          include: ["path"],
        });
        const pathElements = nodeResponse.entry.path?.elements || [];
        const docLibIndex = pathElements.findIndex(
          (el) => el.name === "documentLibrary",
        );
        const displayParts = pathElements
          .slice(docLibIndex + 1)
          .map((el) => el.name);
        displayParts.push(nodeResponse.entry.name);
        noNatureResolvedPath.value = "/ " + displayParts.join(" / ");
      }
    }
  } catch {
    // Fallback: keep null
  }
}

async function initializeActiveMetadata() {
  await nextTick();
  await nextTick();
  const fields = currentFields.value || [];
  const fc = fileConfigs.value[activeFileIndex.value];
  const metadata = {};
  fields.forEach((field) => {
    if (
      parentFolderContext.value?.extractedValues &&
      parentFolderContext.value.extractedValues[field.name] !== undefined
    ) {
      metadata[field.name] =
        parentFolderContext.value.extractedValues[field.name];
    } else if (
      fc?.metadata[field.name] !== undefined &&
      fc?.metadata[field.name] !== null
    ) {
      // Preserve values already set (e.g. extracted from filename)
      metadata[field.name] = fc.metadata[field.name];
    } else if (isRequalifyMode.value && props.currentNode) {
      const existingValue = getExistingFieldValue(field.name);
      metadata[field.name] = existingValue !== undefined ? existingValue : null;
    } else {
      metadata[field.name] = null;
    }
  });
  if (fc) {
    fc.metadata = metadata;
  }
}

/**
 * Initialize metadata for a file using getFieldsForNatureCode (standalone, no composable).
 * Used for inline mode files where we can't rely on activeFileIndex/composable.
 */
function initializeMetadataForFile(index) {
  const fc = fileConfigs.value[index];
  if (!fc?.natureCode) {
    return;
  }
  const fields = getFieldsForNatureCode(fc.natureCode);
  const metadata = {};
  fields.forEach((field) => {
    if (
      parentFolderContext.value?.extractedValues &&
      parentFolderContext.value.extractedValues[field.name] !== undefined
    ) {
      metadata[field.name] =
        parentFolderContext.value.extractedValues[field.name];
    } else if (
      fc.metadata[field.name] !== undefined &&
      fc.metadata[field.name] !== null
    ) {
      // Preserve values already set (e.g. extracted from filename)
      metadata[field.name] = fc.metadata[field.name];
    } else if (
      field.default !== undefined &&
      field.default !== null &&
      field.default !== ""
    ) {
      metadata[field.name] = field.default;
    } else {
      metadata[field.name] = null;
    }
  });
  fc.metadata = metadata;
}

/**
 * For requalify mode: get the existing field value from node properties or parent folder properties.
 */
function getExistingFieldValue(fieldName) {
  if (!activeSelectedNature.value) {
    return undefined;
  }

  const fieldToPropertyMap = {};
  if (activeSelectedNature.value.metadata) {
    for (const [alfrescoProp, valueObj] of Object.entries(
      activeSelectedNature.value.metadata,
    )) {
      const type = Object.keys(valueObj).find((k) => k !== "required");
      if (type === "field") {
        fieldToPropertyMap[valueObj[type]] = alfrescoProp;
      }
    }
  }
  if (
    activeSelectedNature.value.emplacement &&
    planContent.value?.EMPLACEMENT
  ) {
    activeSelectedNature.value.emplacement.forEach((code) => {
      const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
      if (emp?.metadata) {
        for (const [alfrescoProp, valueObj] of Object.entries(emp.metadata)) {
          const type = Object.keys(valueObj).find((k) => k !== "required");
          if (type === "field") {
            fieldToPropertyMap[valueObj[type]] = alfrescoProp;
          }
        }
      }
    });
  }

  const nodeProps = props.currentNode?.properties || {};

  const alfrescoProp = fieldToPropertyMap[fieldName];
  if (alfrescoProp) {
    const val = nodeProps[alfrescoProp];
    if (val !== undefined && val !== null) {
      return val;
    }
  }

  const directVal = nodeProps[fieldName];
  if (directVal !== undefined && directVal !== null) {
    return directVal;
  }

  return undefined;
}

// ─── Multi file handlers ───

function onCategoryChange(index) {
  fileConfigs.value[index].natureCode = null;
  fileConfigs.value[index].metadata = {};
  filenameExtractedFields.value.delete(index);
}

function onNatureChange(index) {
  fileConfigs.value[index].metadata = {};
  filenameExtractedFields.value.delete(index);
  if (fileConfigs.value[index].natureCode === NO_NATURE_CODE) {
    // No nature selected — resolve destination path if not already done
    if (!noNatureResolvedPath.value) {
      resolveNoNaturePath();
    }
    return;
  }
  // Auto-assign category if the file has none and the nature belongs to category(ies)
  if (!fileConfigs.value[index].categoryId) {
    const nature = natureOptions.value.find(
      (n) => n.code === fileConfigs.value[index].natureCode,
    );
    if (nature?.categories?.length) {
      // Pick the first category that exists in importCategories
      const validCat = nature.categories.find((catId) =>
        importCategories.value.some((c) => c.id === catId),
      );
      if (validCat) {
        fileConfigs.value[index].categoryId = validCat;
      }
    }
  }
  // Always initialize metadata synchronously with the correct index
  // to avoid race conditions with the async initializeActiveMetadata
  initializeMetadataForFile(index);
}

function onGlobalCategoryChange() {
  for (let i = 0; i < fileConfigs.value.length; i++) {
    fileConfigs.value[i].categoryId = globalCategoryId.value;
    fileConfigs.value[i].natureCode = null;
    fileConfigs.value[i].metadata = {};
  }
  filenameExtractedFields.value = new Map();
}

function applyBulkCategory() {
  for (const selected of selectedFiles.value) {
    const index = fileConfigs.value.indexOf(selected);
    if (index !== -1) {
      fileConfigs.value[index].categoryId = bulkCategoryId.value;
      fileConfigs.value[index].natureCode = null;
      fileConfigs.value[index].metadata = {};
    }
  }
  selectedFiles.value = [];
  bulkCategoryId.value = null;
}

function applyBulkNature() {
  const natureCode = bulkNatureCode.value;
  if (!natureCode) {
    return;
  }
  for (const selected of selectedFiles.value) {
    const index = fileConfigs.value.indexOf(selected);
    if (index !== -1) {
      fileConfigs.value[index].natureCode = natureCode;
      fileConfigs.value[index].metadata = {};
      if (natureCode === NO_NATURE_CODE) {
        if (!noNatureResolvedPath.value) {
          resolveNoNaturePath();
        }
      } else {
        if (!fileConfigs.value[index].categoryId) {
          const nature = natureOptions.value.find((n) => n.code === natureCode);
          if (nature?.categories?.length) {
            const validCat = nature.categories.find((catId) =>
              importCategories.value.some((c) => c.id === catId),
            );
            if (validCat) {
              fileConfigs.value[index].categoryId = validCat;
            }
          }
        }
        // Always initialize metadata with parent context and defaults
        initializeMetadataForFile(index);
      }
    }
  }
  selectedFiles.value = [];
  bulkNatureCode.value = null;
}

function openMetadataDialog(index) {
  activeFileIndex.value = index;
  metadataDialogIndex.value = index;
  // Re-initialize metadata if it was not populated (e.g. after bulk assignment)
  const fc = fileConfigs.value[index];
  if (
    fc?.natureCode &&
    (!fc.metadata || Object.keys(fc.metadata).length === 0)
  ) {
    initializeMetadataForFile(index);
  }
  showMetadataDialog.value = true;
}

function closeMetadataDialog() {
  showMetadataDialog.value = false;
  if (!isMultiFile.value) {
    activeFileIndex.value = 0;
  }
}

/**
 * Compute destination path for a file at a given index.
 */
function computeDestinationPath(index) {
  const fc = fileConfigs.value[index];
  if (!fc?.natureCode) {
    return "—";
  }

  const nature = natureOptions.value.find((n) => n.code === fc.natureCode);
  if (!nature?.emplacement || !planContent.value?.EMPLACEMENT) {
    return "—";
  }

  const fields = planContent.value?.FIELDS || [];
  const parts = nature.emplacement.map((code) => {
    const emp = planContent.value.EMPLACEMENT.find((e) => e.code === code);
    if (!emp) {
      return `[${code}]`;
    }
    if (emp.metadata?.["cm:name"]) {
      const nameObj = emp.metadata["cm:name"];
      const type = Object.keys(nameObj).find((k) => k !== "required");
      const value = nameObj[type];
      switch (type) {
        case "value":
          return value;
        case "field":
          return fc.metadata[value] || `[${value}]`;
        case "template":
          return resolveTemplate(value, fc.metadata, fields) || `[${code}]`;
        default:
          return emp.name || `[${code}]`;
      }
    }
    return emp.name || `[${code}]`;
  });

  return parts.join("/");
}

// ─── Navigation ───

function nextStep() {
  if (currentStep.value === planStep.value && !isForcedPlan.value) {
    loadPlanContent();
  }
  if (currentStep.value === filesStep.value && !isRequalifyMode.value) {
    if (!planContent.value) {
      loadPlanAndDetectContext();
    }
  }
  if (currentStep.value === filesStep.value && !isMultiFile.value) {
    activeFileIndex.value = 0;
  }
  // Skip config step if noNature (nature → summary directly)
  if (currentStep.value === natureStep.value && noNature.value) {
    currentStep.value = summaryStep.value;
    return;
  }
  if (currentStep.value < summaryStep.value) {
    currentStep.value++;
  }
  // Skip nature step if all files matched the same nature
  if (currentStep.value === natureStep.value && autoDetectedAllMatch.value) {
    currentStep.value = configStep.value;
  }
}

function prevStep() {
  if (currentStep.value > firstStep.value) {
    if (isForcedPlan.value && currentStep.value === firstStep.value) {
      return;
    }
    // Skip config step backward if noNature
    if (currentStep.value === summaryStep.value && noNature.value) {
      currentStep.value = natureStep.value;
      return;
    }
    currentStep.value--;
  }
}

// ─── Plan loading ───

async function loadPlansList() {
  isLoading.value = true;
  try {
    planFiles.value = await classificationService.listPlans();
  } catch (e) {
    console.error("Failed to load classification plans", e);
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: t("classificationPlan.toast.loadPlansError"),
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
}

async function loadPlanContent() {
  if (!selectedPlanName.value) {
    return;
  }
  if (planContent.value) {
    return;
  }

  isLoading.value = true;
  try {
    planContent.value = await classificationService.getPlan(
      selectedPlanName.value,
    );
    await detectParentContext();
    if (props.defaultCategoryId && !globalCategoryId.value) {
      const match = importCategories.value.find(
        (c) => c.id === props.defaultCategoryId,
      );
      if (match) {
        globalCategoryId.value = match.id;
      }
    }
    tryAutoDetectNature();
  } catch (e) {
    console.error("Failed to load plan content", e);
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: t("classificationPlan.toast.loadPlanContentError", {
        plan: selectedPlanName.value,
      }),
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
}

async function loadPlanAndDetectContext() {
  if (planContent.value) {
    return;
  }

  const planName = selectedPlanName.value || props.forcedPlanName;
  if (!planName) {
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: "No classification plan configured",
      life: 5000,
    });
    return;
  }

  isLoading.value = true;
  try {
    planContent.value = await classificationService.getPlan(planName);
    await detectParentContext();
    if (props.defaultCategoryId && !globalCategoryId.value) {
      const match = importCategories.value.find(
        (c) => c.id === props.defaultCategoryId,
      );
      if (match) {
        globalCategoryId.value = match.id;
      }
    }
    tryAutoDetectNature();
  } catch (e) {
    console.error("Failed to load plan", e);
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: t("classificationPlan.toast.loadPlanContentError", {
        plan: planName,
      }),
      life: 3000,
    });
  } finally {
    isLoading.value = false;
  }
}

async function detectParentContext() {
  try {
    const currentNodeId = isRequalifyMode.value
      ? props.currentNode?.id
      : props.defaultDestinationNodeId || navigationStore.currentNode?.id;

    if (!currentNodeId || !planContent.value) {
      return;
    }

    const nodeResponse = await nodesApi.getNode(currentNodeId, {
      include: ["path", "properties"],
    });

    destinationNodeName.value = nodeResponse.entry.name || null;

    const pathElements = nodeResponse.entry.path?.elements || [];

    const sitesIndex = pathElements.findIndex((el) => el.name === "Sites");
    if (sitesIndex !== -1 && pathElements.length > sitesIndex + 1) {
      workspaceSiteId.value = pathElements[sitesIndex + 1].name;
    }

    const docLibIndex = pathElements.findIndex(
      (el) => el.name === "documentLibrary",
    );

    if (docLibIndex === -1) {
      return;
    }

    const parentFolderIds = pathElements
      .slice(docLibIndex + 1)
      .map((el) => el.id);

    const parentFolders = await Promise.all(
      parentFolderIds.map((id) =>
        nodesApi.getNode(id, { include: ["properties"] }),
      ),
    );

    const parentFoldersData = parentFolders.map((response) => ({
      id: response.entry.id,
      name: response.entry.name,
      properties: response.entry.properties || {},
    }));

    parentFoldersData.push({
      id: nodeResponse.entry.id,
      name: nodeResponse.entry.name,
      properties: nodeResponse.entry.properties || {},
    });

    // Merge navigation node path if it differs from the destination node
    // This allows scanning deeper child folders (e.g. dossier marché under consultation)
    if (
      !isRequalifyMode.value &&
      navigationStore.currentNode?.id &&
      navigationStore.currentNode.id !== currentNodeId &&
      props.defaultDestinationNodeId
    ) {
      try {
        const navNodeId = navigationStore.currentNode.id;
        const navResponse = await nodesApi.getNode(navNodeId, {
          include: ["path", "properties"],
        });
        const navPathElements = navResponse.entry.path?.elements || [];
        const navDocLibIndex = navPathElements.findIndex(
          (el) => el.name === "documentLibrary",
        );
        if (navDocLibIndex !== -1) {
          const existingIds = new Set(parentFoldersData.map((f) => f.id));
          const navFolderIds = navPathElements
            .slice(navDocLibIndex + 1)
            .filter((el) => !existingIds.has(el.id));

          if (navFolderIds.length > 0) {
            const navFolders = await Promise.all(
              navFolderIds.map((el) =>
                nodesApi.getNode(el.id, { include: ["properties"] }),
              ),
            );
            for (const resp of navFolders) {
              parentFoldersData.push({
                id: resp.entry.id,
                name: resp.entry.name,
                properties: resp.entry.properties || {},
              });
            }
          }
          // Add the nav node itself if not already present
          if (!existingIds.has(navNodeId)) {
            parentFoldersData.push({
              id: navResponse.entry.id,
              name: navResponse.entry.name,
              properties: navResponse.entry.properties || {},
            });
          }
        }
      } catch (navError) {
        console.warn("Failed to merge navigation path:", navError);
      }
    }

    matchParentFoldersWithPlan(parentFoldersData);
    resolveParentFields(parentFoldersData);
    await searchUnresolvedParentFields(currentNodeId);
  } catch (error) {
    console.error("Failed to detect parent context:", error);
  }
}

function matchParentFoldersWithPlan(parentFolders) {
  if (!planContent.value?.EMPLACEMENT) {
    return;
  }

  const matchedEmplacements = [];
  const extractedValues = {};
  const matchedFields = new Set();

  for (const emp of planContent.value.EMPLACEMENT) {
    if (!emp.metadata) {
      continue;
    }

    const empMetadataKeys = Object.keys(emp.metadata);

    for (const folder of parentFolders) {
      let matchCount = 0;
      const folderValues = {};

      for (const key of empMetadataKeys) {
        if (folder.properties[key] !== undefined) {
          matchCount++;
          folderValues[key] = folder.properties[key];
        }
      }

      if (matchCount > 0) {
        matchedEmplacements.push({
          emplacementCode: emp.code,
          folderId: folder.id,
          folderName: folder.name,
          metadataValues: folderValues,
        });

        for (const [key, valueObj] of Object.entries(emp.metadata)) {
          const type = Object.keys(valueObj).find((k) => k !== "required");

          if (type === "field") {
            const fieldName = valueObj[type];
            const metadataValue = folder.properties[key];

            if (metadataValue !== undefined) {
              extractedValues[fieldName] = metadataValue;
              matchedFields.add(fieldName);
            }
          }
        }

        break;
      }
    }
  }

  parentFolderContext.value = {
    matchedEmplacements,
    extractedValues,
    parentFoldersData: parentFolders,
  };

  // In requalify mode, keep fields editable so the user can change e.g. lot number
  if (!isRequalifyMode.value) {
    preFilledFields.value = matchedFields;
  }
}

function resolveParentFields(parentFoldersData) {
  if (!planContent.value?.FIELDS) {
    return;
  }
  if (!parentFolderContext.value) {
    parentFolderContext.value = {
      matchedEmplacements: [],
      extractedValues: {},
      parentFoldersData,
    };
  }
  const reversedFolders = [...parentFoldersData].reverse();
  const newExtracted = {};
  const newPreFilled = [];
  for (const field of planContent.value.FIELDS) {
    if (field.input !== "parent") {
      continue;
    }
    if (!field.metadata) {
      continue;
    }
    for (const folder of reversedFolders) {
      const value = folder.properties[field.metadata];
      if (value !== undefined && value !== null) {
        newExtracted[field.name] = value;
        newPreFilled.push(field.name);
        break;
      }
    }
  }
  if (newPreFilled.length > 0) {
    Object.assign(parentFolderContext.value.extractedValues, newExtracted);
    preFilledFields.value = new Set([
      ...preFilledFields.value,
      ...newPreFilled,
    ]);
  }
}

async function searchUnresolvedParentFields(currentNodeId) {
  if (!planContent.value?.FIELDS) {
    return;
  }

  const unresolvedFields = planContent.value.FIELDS.filter(
    (field) =>
      field.input === "parent" &&
      field.metadata &&
      field.fallbackQuery &&
      !parentFolderContext.value?.extractedValues?.[field.name],
  );

  if (unresolvedFields.length === 0) {
    return;
  }

  for (const field of unresolvedFields) {
    try {
      // Substitute variables in the fallback query
      const resolvedQuery = field.fallbackQuery
        .replace(/\$\{nodeId\}/g, currentNodeId)
        .replace(/\$\{siteId\}/g, workspaceSiteId.value || "");

      const query = {
        query: {
          language: "afts",
          query: resolvedQuery,
        },
        include: ["properties"],
      };
      const results = await searchApi.search(query);
      const entries = results?.list?.entries || [];

      if (entries.length === 1) {
        // Single result → auto-fill
        const nodeProps = entries[0].entry.properties || {};
        const value = nodeProps[field.metadata];
        if (value !== undefined && value !== null) {
          if (!parentFolderContext.value) {
            parentFolderContext.value = {
              matchedEmplacements: [],
              extractedValues: {},
              parentFoldersData: [],
            };
          }
          parentFolderContext.value.extractedValues[field.name] = value;
          preFilledFields.value = new Set([
            ...preFilledFields.value,
            field.name,
          ]);
        }
      } else if (entries.length > 1) {
        // Multiple results → offer dropdown options
        const options = [];
        for (const entry of entries) {
          const nodeProps = entry.entry.properties || {};
          const value = nodeProps[field.metadata];
          if (value !== undefined && value !== null) {
            options.push({
              label: value,
              value: value,
            });
          }
        }
        if (options.length > 0) {
          parentFieldOptions.value = {
            ...parentFieldOptions.value,
            [field.name]: options,
          };
        }
      }
      // 0 results → nothing, existing error message will show
    } catch (searchError) {
      console.warn(
        `Failed to search for parent field "${field.name}":`,
        searchError,
      );
    }
  }
}

// ─── Deposit / Requalify ───

async function doDeposit() {
  if (isRequalifyMode.value) {
    await doRequalify();
  } else if (isCreateMode.value) {
    await doCreateFile();
  } else {
    await doUpload();
  }
}

/**
 * Deduplicate filenames in perFileData by checking batch duplicates
 * and existing files on the server.
 */
async function deduplicatePerFileData(perFileData) {
  // Group entries by destination folder (pathFull key)
  const groups = new Map();
  for (let i = 0; i < perFileData.length; i++) {
    const key = JSON.stringify(perFileData[i].pathFull);
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(i);
  }

  // Determine the root node ID
  let rootId;
  if (props.defaultDestinationNodeId) {
    rootId = props.defaultDestinationNodeId;
  } else {
    rootId = await classificationFileService.getDocumentLibraryId(
      workspaceSiteId.value,
    );
  }

  for (const [, indices] of groups) {
    const pathFull = perFileData[indices[0]].pathFull;
    const newNames = indices.map((i) => perFileData[i].fileRenames[0].new);

    // Resolve folder hierarchy and list existing files
    let existingNames = [];
    try {
      const targetFolderId =
        await classificationFileService.ensureFolderHierarchy(rootId, pathFull);
      const result = await alfrescoNodeService.getDirectChildren(
        targetFolderId,
        { maxItems: 1000 },
      );
      existingNames = result.children.map((child) => child.name);
    } catch {
      // If folder doesn't exist yet, no existing names to worry about
    }

    const deduped = deduplicateFileNames(newNames, existingNames);
    for (let j = 0; j < indices.length; j++) {
      perFileData[indices[j]].fileRenames[0].new = deduped[j];
    }
  }
}

async function doUpload() {
  const allFilesNoNature =
    noNature.value ||
    fileConfigs.value.every((fc) => fc.natureCode === NO_NATURE_CODE);
  if (!workspaceSiteId.value && !allFilesNoNature) {
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: t("classificationPlan.toast.workspaceError"),
      life: 5000,
    });
    return;
  }

  isDepositing.value = true;

  try {
    const uploadStore = useUploadStore();
    uploadStore.onCompleteCallback = () => emit("upload-complete");

    // No nature (single file mode) → simple upload to current folder
    if (noNature.value) {
      const targetNodeId =
        props.defaultDestinationNodeId || navigationStore.currentNode?.id;
      const uploadItems = fileConfigs.value.map((fc) => ({
        file: fc.file,
        relativePath: "",
      }));
      uploadStore.startUpload(uploadItems, targetNodeId);
    } else {
      const fields = planContent.value?.FIELDS || [];
      const targetNodeId =
        props.defaultDestinationNodeId || navigationStore.currentNode?.id;

      // Separate files with nature from files without nature
      const noNatureFiles = fileConfigs.value.filter(
        (fc) => fc.natureCode === NO_NATURE_CODE,
      );
      const naturedFiles = fileConfigs.value.filter(
        (fc) => fc.natureCode !== NO_NATURE_CODE,
      );

      // Upload files without nature as simple uploads
      if (noNatureFiles.length > 0) {
        const uploadItems = noNatureFiles.map((fc) => ({
          file: fc.file,
          relativePath: "",
        }));
        uploadStore.startUpload(uploadItems, targetNodeId);
      }

      // Upload files with nature via classification plan
      if (naturedFiles.length > 0) {
        const allFiles = [];
        const perFileData = [];

        for (const fc of naturedFiles) {
          const origIndex = fileConfigs.value.indexOf(fc);
          const nature = natureOptions.value.find(
            (n) => n.code === fc.natureCode,
          );

          const pathFull = computePathFull(origIndex);
          const metadata = computeFileMetadata(origIndex);

          const nameTemplate = nature?.metadata?.["cm:name"]?.template;
          let newName = fc.file.name;
          if (nameTemplate) {
            const resolved = resolveTemplate(nameTemplate, fc.metadata, fields);
            if (resolved) {
              const dotIndex = fc.file.name.lastIndexOf(".");
              const extension =
                dotIndex > 0 ? fc.file.name.substring(dotIndex) : "";
              newName = resolved + extension;
            }
          }

          allFiles.push(fc.file);
          perFileData.push({
            pathFull,
            metadata,
            aspects: nature?.aspects || [],
            fileRenames: [{ original: fc.file.name, new: newName }],
          });
        }

        // Deduplicate filenames if strategy is "increment"
        if (props.duplicateNameStrategy === "increment") {
          await deduplicatePerFileData(perFileData);
        }

        uploadStore.startUploadWithClassificationPlanMulti(
          workspaceSiteId.value,
          allFiles,
          perFileData,
          props.defaultDestinationNodeId,
        );
      }
    }

    toast.add({
      severity: "success",
      summary: t("success"),
      detail: t("classificationPlan.toast.depositSuccess"),
      life: 3000,
    });
    isVisible.value = false;
  } catch (e) {
    console.error("Deposit error", e);
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: t("classificationPlan.toast.depositError"),
      life: 5000,
    });
  } finally {
    isDepositing.value = false;
  }
}

async function doCreateFile() {
  if (!workspaceSiteId.value) {
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: t("classificationPlan.toast.workspaceError"),
      life: 5000,
    });
    return;
  }

  isDepositing.value = true;

  try {
    const createInfo = props.createFileMode;
    const fc = fileConfigs.value[0];
    const nature = natureOptions.value.find((n) => n.code === fc.natureCode);
    const pathFull = computePathFull(0);
    const metadata = computeFileMetadata(0);

    const nameTemplate = nature?.metadata?.["cm:name"]?.template;
    const finalName = nameTemplate
      ? resolveTemplate(
          nameTemplate,
          fc.metadata,
          planContent.value?.FIELDS || [],
        ) + (createInfo.fileExtension ? `.${createInfo.fileExtension}` : "")
      : createInfo.fileName;

    const node = await classificationFileService.createFileWithPlan(
      workspaceSiteId.value,
      pathFull,
      createInfo.templateNodeId,
      finalName,
      metadata,
      nature,
    );

    isVisible.value = false;

    emit("file-created", {
      nodeId: node.entry.id,
      fileType: createInfo.fileType,
    });

    toast.add({
      severity: "success",
      summary: t("success"),
      detail: t("classificationPlan.toast.createSuccess"),
      life: 3000,
    });
  } catch (error) {
    console.error("Create file error:", error);
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: error.message || t("classificationPlan.toast.createError"),
      life: 5000,
    });
  } finally {
    isDepositing.value = false;
  }
}

async function doRequalify() {
  if (!workspaceSiteId.value) {
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: t("classificationPlan.toast.workspaceError"),
      life: 5000,
    });
    return;
  }

  isDepositing.value = true;

  try {
    const fc = fileConfigs.value[0];
    const nature = natureOptions.value.find((n) => n.code === fc.natureCode);
    const pathFull = computePathFull(0);
    const metadata = computeFileMetadata(0);
    const newFileName = computeFileRename(0);

    await classificationFileService.requalifyDocument(
      props.currentNode.id,
      workspaceSiteId.value,
      pathFull,
      metadata,
      newFileName,
      nature,
      props.defaultDestinationNodeId,
    );

    toast.add({
      severity: "success",
      summary: t("success"),
      detail: t("classificationPlan.toast.requalifySuccess"),
      life: 3000,
    });
    isVisible.value = false;
  } catch (error) {
    console.error("Requalification failed:", error);
    toast.add({
      severity: "error",
      summary: t("error"),
      detail: error.message || t("classificationPlan.toast.requalifyError"),
      life: 5000,
    });
  } finally {
    isDepositing.value = false;
  }
}

// ─── Reset ───

function reset() {
  currentStep.value = isForcedPlan.value ? 1 : 1;
  multiCategoryMode.value = !props.forceSingleCategory;
  globalCategoryId.value = null;
  fileConfigs.value = [];
  selectedFiles.value = [];
  bulkCategoryId.value = null;
  bulkNatureCode.value = null;
  statusFilter.value = "all";
  fullViewThreshold.value =
    configStore.CLASSIFICATION_PLAN_FULL_VIEW_THRESHOLD || 5;
  inlineMetadataLayout.value = "row";
  expandedGroups.value = [];
  activeFileIndex.value = 0;
  planContent.value = null;
  workspaceSiteId.value = null;
  isDepositing.value = false;
  showMetadataDialog.value = false;
  metadataDialogIndex.value = null;
  noNature.value = false;
  noNatureResolvedPath.value = null;
  autoDetectedAllMatch.value = false;
  filenameExtractedFields.value = new Map();
  parentFolderContext.value = null;
  preFilledFields.value = new Set();
  parentFieldOptions.value = {};
  selectedPlanName.value = null;
  planFiles.value = [];
  destinationNodeName.value = null;
  resetClassification();
  emit("close");
}

// ─── Watch visibility ───

watch(
  () => props.visible,
  (val) => {
    if (val) {
      // Resolve destination node name
      if (props.defaultDestinationNodeId) {
        nodesApi
          .getNode(props.defaultDestinationNodeId)
          .then((res) => {
            destinationNodeName.value = res.entry.name;
          })
          .catch(() => {});
      }

      if (isForcedPlan.value) {
        selectedPlanName.value = props.forcedPlanName;

        if (isRequalifyMode.value) {
          currentStep.value = natureStep.value;
          fileConfigs.value = [createFileConfig(props.currentNode)];
          loadPlanContent();
        } else if (isCreateMode.value) {
          currentStep.value = configStep.value;
          fileConfigs.value = [
            createFileConfig({ name: props.createFileMode.fileName }),
          ];
          loadPlanContent();
        } else {
          currentStep.value = filesStep.value;
          if (props.filesToUpload && props.filesToUpload.length > 0) {
            fileConfigs.value = props.filesToUpload.map((f) =>
              createFileConfig(f),
            );
          }
          loadPlanContent();
        }
      } else {
        currentStep.value = planStep.value;

        if (isRequalifyMode.value) {
          fileConfigs.value = [createFileConfig(props.currentNode)];
        } else if (isCreateMode.value) {
          fileConfigs.value = [
            createFileConfig({ name: props.createFileMode.fileName }),
          ];
        }
        loadPlansList();
      }
    }
    if (!val) {
      reset();
    }
  },
  { immediate: true },
);
</script>

<style scoped>
.unified-upload-dialog {
  width: 100%;
}

.stepper-header {
  margin-bottom: 1rem;
}

.stepper-indicator {
  margin-bottom: 0.75rem;
}

.step-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.step-plan-info {
  display: block;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin-bottom: 0.5rem;
}

.loading-container {
  text-align: center;
  padding: 2rem;
}

.full-width {
  width: 100%;
}

.upload-file-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.upload-file-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--p-content-border-color);
}

.upload-file-item:last-child {
  border-bottom: none;
}

.upload-file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-file-size {
  color: var(--p-text-muted-color);
  font-size: 0.85rem;
  white-space: nowrap;
}

.no-files-msg {
  text-align: center;
  color: var(--p-text-muted-color);
  padding: 1rem;
}

/* Single file mode */
.single-file-config {
  min-height: 10rem;
}

.selected-nature-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

/* No nature destination */
.no-nature-destination {
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: var(--p-surface-ground);
  border-radius: var(--p-border-radius);
}

.no-nature-destination-label {
  margin: 0 0 0.25rem 0;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
}

.no-nature-destination-path {
  font-size: 0.9rem;
}

/* Create file mode */
.create-file-info {
  margin-top: 0.75rem;
}

/* Multi file mode */
.multi-file-config {
  min-height: 10rem;
}

.multi-file-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.bulk-toolbar {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: var(--p-border-radius);
  background: color-mix(
    in srgb,
    var(--p-primary-color) 8%,
    var(--p-content-background)
  );
}

.bulk-selection-count {
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
}

/* Summary */
.summary-file-panel {
  margin-bottom: 0.75rem;
}

.summary-panel-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.summary-panel-filename {
  font-weight: 600;
}

.summary-section h5 {
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
}

.summary-section p {
  margin: 0.25rem 0;
}

.summary-section code {
  font-size: 0.9rem;
}

.summary-metadata-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.summary-metadata-list li {
  padding: 0.15rem 0;
}

.arrow-icon {
  margin: 0 0.5rem;
}

.category-mode-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.global-category-select {
  flex: 1;
  max-width: 20rem;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.progress-bar-label {
  font-size: 12px;
  color: var(--p-text-muted-color);
  white-space: nowrap;
}

.progress-bar {
  flex: 1;
}

.multi-file-filters {
  margin-bottom: 0.5rem;
}

:deep(.p-datatable-row-group-header td > button) {
  vertical-align: middle;
  margin-right: 0.5rem;
}

:deep(.p-datatable-row-group-header td > .category-group-header) {
  display: inline-flex;
  vertical-align: middle;
}

.category-group-header {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 1rem;
  font-weight: 700;
}

.category-group-count {
  color: var(--p-text-muted-color);
}

/* Inline metadata */
.inline-metadata {
  display: flex;
  gap: 0.5rem;
  padding: 0.25rem 0;
  min-width: 0;
  overflow: hidden;
  container-type: inline-size;
}
.inline-metadata--row {
  flex-direction: row;
}
.inline-metadata--col {
  flex-direction: column;
}
/* Auto-switch to column when the container is too narrow for row layout */
@container (max-width: 240px) {
  .inline-metadata--row {
    flex-direction: column;
  }
}
.inline-metadata-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  overflow: hidden;
}
.inline-metadata--row .inline-metadata-field {
  flex: 1 1 0;
  min-width: 0;
}
.inline-metadata--col .inline-metadata-field {
  width: 100%;
}
/* Constrain all PrimeVue inputs inside inline fields to prevent overflow */
.inline-metadata-field :deep(.p-inputtext),
.inline-metadata-field :deep(.p-select),
.inline-metadata-field :deep(.p-datepicker),
.inline-metadata-field :deep(.p-inputnumber),
.inline-metadata-field :deep(.p-dropdown) {
  min-width: 0;
  width: 100%;
}
.inline-metadata-label {
  font-size: 10px;
  color: var(--p-text-muted-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  display: block;
}
.inline-metadata-required {
  color: var(--p-red-500);
  font-weight: 600;
}
.auto-extracted-icon {
  margin-left: 0.25rem;
  color: var(--p-primary-color);
  cursor: help;
}
.inline-metadata-default {
  font-size: 9px;
  color: var(--p-text-muted-color);
  line-height: 1;
}
.inline-field-na {
  color: var(--p-text-muted-color);
}

/* Metadata sub-dialog auto-size */
:deep(.metadata-sub-dialog .p-dialog-content) {
  overflow-y: auto;
}

/* Inline mode */
.unified-upload-inline {
  width: 100%;
}

.upload-inline-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;
  margin-top: 1rem;
  border-top: 1px solid var(--p-content-border-color);
}
</style>
